/**
 * Zustand Store for AI Role Configuration
 * New architecture: API Configuration Pool + Role Assignments
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  InterpreterConfig,
  LogicCheckerConfig,
  SolutionGeneratorConfig,
  ResultValidatorConfig,
  RoleConfigurations,
  AIProvider,
} from '@shared/types';

// API Configuration for each provider
export interface APIConfig {
  provider: AIProvider;
  model: string;
  apiKey: string;
  temperature: number;
  maxTokens?: number;
  baseUrl?: string;
}

// Available API providers
export type APIProviderKey = 'deepseek' | 'doubao' | 'qwen' | 'openai' | 'anthropic';

interface ConfigStore {
  // API Configurations Pool
  apiConfigs: {
    deepseek: APIConfig | null;
    doubao: APIConfig | null;
    qwen: APIConfig | null;
    openai: APIConfig | null;
    anthropic: APIConfig | null;
  };

  // Role Assignments (which API each role uses)
  roleAssignments: {
    interpreter: APIProviderKey | null;
    logicChecker: APIProviderKey | null;
    solutionGenerator: APIProviderKey | null;
    resultValidator: APIProviderKey | null;
  };

  // Solution Generator specific options
  solutionGeneratorOptions: {
    detailLevel: 'basic' | 'detailed' | 'comprehensive';
    stepByStep: boolean;
    includeVerification: boolean;
  };

  // Actions for API Configs
  setAPIConfig: (provider: APIProviderKey, config: APIConfig | null) => void;

  // Actions for Role Assignments
  setRoleAssignment: (
    role: 'interpreter' | 'logicChecker' | 'solutionGenerator' | 'resultValidator',
    apiProvider: APIProviderKey | null
  ) => void;

  // Actions for Solution Generator Options
  setSolutionGeneratorOptions: (options: Partial<{
    detailLevel: 'basic' | 'detailed' | 'comprehensive';
    stepByStep: boolean;
    includeVerification: boolean;
  }>) => void;

  // Legacy compatibility - get configurations in old format for backend
  interpreter: InterpreterConfig | null;
  logicChecker: LogicCheckerConfig | null;
  solutionGenerator: SolutionGeneratorConfig | null;
  resultValidator: ResultValidatorConfig | null;
  setInterpreter: (config: InterpreterConfig) => void;
  setLogicChecker: (config: LogicCheckerConfig) => void;
  setSolutionGenerator: (config: SolutionGeneratorConfig) => void;
  setResultValidator: (config: ResultValidatorConfig) => void;
  setAllConfigurations: (configs: RoleConfigurations) => void;

  // Validation and retrieval
  isConfigured: () => boolean;
  getAllConfigurations: () => RoleConfigurations | null;
  isAPIConfigured: (provider: APIProviderKey) => boolean;
}

const createDefaultSolutionGeneratorOptions = () => ({
  detailLevel: 'detailed' as const,
  stepByStep: true,
  includeVerification: true,
});

export const useConfigStore = create<ConfigStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        apiConfigs: {
          deepseek: null,
          doubao: null,
          qwen: null,
          openai: null,
          anthropic: null,
        },

        roleAssignments: {
          interpreter: null,
          logicChecker: null,
          solutionGenerator: null,
          resultValidator: null,
        },

        solutionGeneratorOptions: createDefaultSolutionGeneratorOptions(),

        // Legacy compatibility
        interpreter: null,
        logicChecker: null,
        solutionGenerator: null,
        resultValidator: null,

        // API Config Actions
        setAPIConfig: (provider, config) => {
          set((state) => ({
            apiConfigs: {
              ...state.apiConfigs,
              [provider]: config,
            },
          }));
        },

        // Role Assignment Actions
        setRoleAssignment: (role, apiProvider) => {
          set((state) => ({
            roleAssignments: {
              ...state.roleAssignments,
              [role]: apiProvider,
            },
          }));
        },

        // Solution Generator Options Actions
        setSolutionGeneratorOptions: (options) => {
          set((state) => ({
            solutionGeneratorOptions: {
              ...state.solutionGeneratorOptions,
              ...options,
            },
          }));
        },

        // Legacy Actions (for backward compatibility)
        setInterpreter: (config) => set({ interpreter: config }),
        setLogicChecker: (config) => set({ logicChecker: config }),
        setSolutionGenerator: (config) => set({ solutionGenerator: config }),
        setResultValidator: (config) => set({ resultValidator: config }),
        setAllConfigurations: (configs) => set({
          interpreter: configs.interpreter,
          logicChecker: configs.logicChecker,
          solutionGenerator: configs.solutionGenerator,
          resultValidator: configs.resultValidator,
        }),

        // Validation
        isAPIConfigured: (provider) => {
          const state = get();
          const config = state.apiConfigs[provider];
          return !!(config && config.apiKey);
        },

        isConfigured: () => {
          const state = get();
          const { roleAssignments, apiConfigs } = state;

          // Check if all roles are assigned
          if (!roleAssignments.interpreter || !roleAssignments.logicChecker ||
              !roleAssignments.solutionGenerator || !roleAssignments.resultValidator) {
            return false;
          }

          // Check if assigned APIs are configured
          const assignedAPIs = new Set([
            roleAssignments.interpreter,
            roleAssignments.logicChecker,
            roleAssignments.solutionGenerator,
            roleAssignments.resultValidator,
          ]);

          for (const api of assignedAPIs) {
            if (!api || !apiConfigs[api] || !apiConfigs[api]?.apiKey) {
              return false;
            }
          }

          return true;
        },

        // Get all configurations in backend format
        getAllConfigurations: () => {
          const state = get();
          const { roleAssignments, apiConfigs, solutionGeneratorOptions } = state;

          if (!state.isConfigured()) {
            return null;
          }

          const interpreterAPI = roleAssignments.interpreter!;
          const logicCheckerAPI = roleAssignments.logicChecker!;
          const solutionGeneratorAPI = roleAssignments.solutionGenerator!;
          const resultValidatorAPI = roleAssignments.resultValidator!;

          const interpreterConfig = apiConfigs[interpreterAPI]!;
          const logicCheckerConfig = apiConfigs[logicCheckerAPI]!;
          const solutionGeneratorConfig = apiConfigs[solutionGeneratorAPI]!;
          const resultValidatorConfig = apiConfigs[resultValidatorAPI]!;

          return {
            interpreter: {
              role: 'interpreter',
              apiProvider: interpreterConfig.provider,
              model: interpreterConfig.model,
              apiKey: interpreterConfig.apiKey,
              temperature: interpreterConfig.temperature,
              maxTokens: interpreterConfig.maxTokens,
              baseUrl: interpreterConfig.baseUrl,
            } as InterpreterConfig,

            logicChecker: {
              role: 'logic_checker',
              apiProvider: logicCheckerConfig.provider,
              model: logicCheckerConfig.model,
              apiKey: logicCheckerConfig.apiKey,
              temperature: logicCheckerConfig.temperature,
              maxTokens: logicCheckerConfig.maxTokens,
              baseUrl: logicCheckerConfig.baseUrl,
            } as LogicCheckerConfig,

            solutionGenerator: {
              role: 'solution_generator',
              apiProvider: solutionGeneratorConfig.provider,
              model: solutionGeneratorConfig.model,
              apiKey: solutionGeneratorConfig.apiKey,
              temperature: solutionGeneratorConfig.temperature,
              maxTokens: solutionGeneratorConfig.maxTokens,
              baseUrl: solutionGeneratorConfig.baseUrl,
              detailLevel: solutionGeneratorOptions.detailLevel,
              stepByStep: solutionGeneratorOptions.stepByStep,
              includeVerification: solutionGeneratorOptions.includeVerification,
            } as SolutionGeneratorConfig,

            resultValidator: {
              role: 'result_validator',
              apiProvider: resultValidatorConfig.provider,
              model: resultValidatorConfig.model,
              apiKey: resultValidatorConfig.apiKey,
              temperature: resultValidatorConfig.temperature,
              maxTokens: resultValidatorConfig.maxTokens,
              baseUrl: resultValidatorConfig.baseUrl,
            } as ResultValidatorConfig,
          };
        },
      }),
      {
        name: 'config-store',
      }
    )
  )
);
